/**
 * URL parameter parsing and generation utilities
 */

import type { RouteParams, SearchParams, ValidationResult } from '../../types/routing'

/**
 * Parse route parameters from URL path
 */
export const parseRouteParams = (pathname: string): RouteParams => {
  const params: RouteParams = {}
  
  // Parse CPU detail route: /cpu/:id
  const cpuDetailMatch = pathname.match(/^\/cpu\/([^/]+)$/)
  if (cpuDetailMatch) {
    params.id = cpuDetailMatch[1]
  }
  
  // Parse category route: /category/:category
  const categoryMatch = pathname.match(/^\/category\/([^/]+)$/)
  if (categoryMatch) {
    const category = categoryMatch[1]
    if (['gaming', 'workstation', 'budget', 'high-end', 'server'].includes(category)) {
      params.category = category as NonNullable<RouteParams['category']>
    }
  }
  
  return params
}

/**
 * Parse search parameters from URL search string
 */
export const parseSearchParams = (searchString: string): SearchParams => {
  const urlParams = new URLSearchParams(searchString)
  const params: SearchParams = {}
  
  // String parameters
  if (urlParams.has('search')) {
    params.search = urlParams.get('search')!
  }
  
  if (urlParams.has('cpus')) {
    params.cpus = urlParams.get('cpus')!
  }
  
  if (urlParams.has('sort')) {
    const sort = urlParams.get('sort')
    if (sort && ['price-asc', 'price-desc', 'name-asc', 'name-desc', 'performance-desc'].includes(sort)) {
      params.sort = sort as NonNullable<SearchParams['sort']>
    }
  }
  
  // Numeric parameters
  if (urlParams.has('priceMin')) {
    const priceMin = parseInt(urlParams.get('priceMin')!, 10)
    if (!isNaN(priceMin) && priceMin >= 0) {
      params.priceMin = priceMin
    }
  }
  
  if (urlParams.has('priceMax')) {
    const priceMax = parseInt(urlParams.get('priceMax')!, 10)
    if (!isNaN(priceMax) && priceMax >= 0) {
      params.priceMax = priceMax
    }
  }
  
  if (urlParams.has('tdpMin')) {
    const tdpMin = parseInt(urlParams.get('tdpMin')!, 10)
    if (!isNaN(tdpMin) && tdpMin >= 15) {
      params.tdpMin = tdpMin
    }
  }
  
  if (urlParams.has('tdpMax')) {
    const tdpMax = parseInt(urlParams.get('tdpMax')!, 10)
    if (!isNaN(tdpMax) && tdpMax <= 500) {
      params.tdpMax = tdpMax
    }
  }
  
  if (urlParams.has('page')) {
    const page = parseInt(urlParams.get('page')!, 10)
    if (!isNaN(page) && page >= 1) {
      params.page = page
    }
  }
  
  // Array parameters
  if (urlParams.has('cores')) {
    const coresStr = urlParams.get('cores')!
    const cores = coresStr.split(',').map(c => parseInt(c, 10)).filter(c => !isNaN(c) && c > 0)
    if (cores.length > 0) {
      params.cores = cores
    }
  }
  
  if (urlParams.has('socket')) {
    const socketStr = urlParams.get('socket')!
    const sockets = socketStr.split(',').filter(s => s.trim().length > 0)
    if (sockets.length > 0) {
      params.socket = sockets
    }
  }
  
  return params
}

/**
 * Generate URL search string from search parameters
 */
export const generateSearchParams = (params: Partial<SearchParams>): string => {
  const urlParams = new URLSearchParams()
  
  // Add string parameters
  if (params.search) {
    urlParams.set('search', params.search)
  }
  
  if (params.cpus) {
    urlParams.set('cpus', params.cpus)
  }
  
  if (params.sort) {
    urlParams.set('sort', params.sort)
  }
  
  // Add numeric parameters
  if (params.priceMin !== undefined) {
    urlParams.set('priceMin', params.priceMin.toString())
  }
  
  if (params.priceMax !== undefined) {
    urlParams.set('priceMax', params.priceMax.toString())
  }
  
  if (params.tdpMin !== undefined) {
    urlParams.set('tdpMin', params.tdpMin.toString())
  }
  
  if (params.tdpMax !== undefined) {
    urlParams.set('tdpMax', params.tdpMax.toString())
  }
  
  if (params.page !== undefined) {
    urlParams.set('page', params.page.toString())
  }
  
  // Add array parameters
  if (params.cores && params.cores.length > 0) {
    urlParams.set('cores', params.cores.join(','))
  }
  
  if (params.socket && params.socket.length > 0) {
    urlParams.set('socket', params.socket.join(','))
  }
  
  const searchString = urlParams.toString()
  return searchString ? `?${searchString}` : ''
}

/**
 * Generate URL for specific routes
 */
export const generateRouteUrl = (route: string, params?: RouteParams, searchParams?: Partial<SearchParams>): string => {
  let url = route
  
  // Replace route parameters
  if (params?.id) {
    url = url.replace(':id', params.id)
  }
  
  if (params?.category) {
    url = url.replace(':category', params.category)
  }
  
  // Add search parameters
  if (searchParams) {
    const searchString = generateSearchParams(searchParams)
    url += searchString
  }
  
  return url
}

/**
 * Validate CPU ID format
 */
export const validateCpuId = (id: string): boolean => {
  const pattern = /^[a-z0-9]+(-[a-z0-9]+)*$/
  return pattern.test(id)
}

/**
 * Validate category parameter
 */
export const validateCategory = (category: string): boolean => {
  const validCategories = ['gaming', 'workstation', 'budget', 'high-end', 'server']
  return validCategories.includes(category)
}

/**
 * Validate search parameters
 */
export const validateSearchParams = (params: SearchParams): ValidationResult => {
  const errors: string[] = []
  const warnings: string[] = []
  
  // Validate price ranges
  if (params.priceMin !== undefined) {
    if (params.priceMin < 0) {
      errors.push('Price minimum cannot be negative')
    }
    if (params.priceMin > 10000) {
      warnings.push('Price minimum is very high')
    }
  }
  
  if (params.priceMax !== undefined) {
    if (params.priceMax < 0) {
      errors.push('Price maximum cannot be negative')
    }
    if (params.priceMax > 10000) {
      warnings.push('Price maximum is very high')
    }
  }
  
  if (params.priceMin !== undefined && params.priceMax !== undefined) {
    if (params.priceMin > params.priceMax) {
      errors.push('Price minimum cannot be greater than maximum')
    }
  }
  
  // Validate TDP ranges
  if (params.tdpMin !== undefined) {
    if (params.tdpMin < 15) {
      errors.push('TDP minimum too low (minimum 15W)')
    }
    if (params.tdpMin > 500) {
      errors.push('TDP minimum too high (maximum 500W)')
    }
  }
  
  if (params.tdpMax !== undefined) {
    if (params.tdpMax < 15) {
      errors.push('TDP maximum too low (minimum 15W)')
    }
    if (params.tdpMax > 500) {
      errors.push('TDP maximum too high (maximum 500W)')
    }
  }
  
  if (params.tdpMin !== undefined && params.tdpMax !== undefined) {
    if (params.tdpMin > params.tdpMax) {
      errors.push('TDP minimum cannot be greater than maximum')
    }
  }
  
  // Validate core counts
  if (params.cores) {
    const invalidCores = params.cores.filter(c => c < 1 || c > 128)
    if (invalidCores.length > 0) {
      errors.push(`Invalid core counts: ${invalidCores.join(', ')}`)
    }
  }
  
  // Validate page number
  if (params.page !== undefined) {
    if (params.page < 1) {
      errors.push('Page number must be at least 1')
    }
    if (params.page > 1000) {
      warnings.push('Large page number may have no results')
    }
  }
  
  // Validate sort parameter
  if (params.sort) {
    const validSorts = ['price-asc', 'price-desc', 'name-asc', 'name-desc', 'performance-desc']
    if (!validSorts.includes(params.sort)) {
      errors.push(`Invalid sort option: ${params.sort}`)
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  }
}

/**
 * Validate comparison CPU parameters
 */
export const validateComparisonCpus = (cpus: string[]): ValidationResult => {
  const errors: string[] = []
  const warnings: string[] = []
  
  // Check count constraints
  if (cpus.length < 2) {
    errors.push('Comparison requires at least 2 CPUs')
  }
  
  if (cpus.length > 5) {
    errors.push('Comparison limited to maximum 5 CPUs')
  }
  
  // Validate each CPU ID
  const invalidIds = cpus.filter(id => !validateCpuId(id))
  if (invalidIds.length > 0) {
    errors.push(`Invalid CPU IDs: ${invalidIds.join(', ')}`)
  }
  
  // Check for duplicates
  const duplicates = cpus.filter((cpu, index) => cpus.indexOf(cpu) !== index)
  if (duplicates.length > 0) {
    warnings.push(`Duplicate CPUs removed: ${duplicates.join(', ')}`)
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  }
}

/**
 * Clean and normalize URL parameters
 */
export const cleanUrlParams = (params: Partial<SearchParams>): Partial<SearchParams> => {
  const cleaned: Partial<SearchParams> = {}
  
  // Clean string parameters
  if (params.search?.trim()) {
    cleaned.search = params.search.trim()
  }
  
  if (params.cpus?.trim()) {
    // Remove duplicates and validate CPU IDs
    const cpuList = params.cpus.split(',').map(id => id.trim()).filter(id => validateCpuId(id))
    const uniqueCpus = [...new Set(cpuList)]
    if (uniqueCpus.length >= 2 && uniqueCpus.length <= 5) {
      cleaned.cpus = uniqueCpus.join(',')
    }
  }
  
  if (params.sort) {
    cleaned.sort = params.sort
  }
  
  // Clean numeric parameters
  if (params.priceMin !== undefined && params.priceMin >= 0 && params.priceMin <= 10000) {
    cleaned.priceMin = params.priceMin
  }
  
  if (params.priceMax !== undefined && params.priceMax >= 0 && params.priceMax <= 10000) {
    cleaned.priceMax = params.priceMax
  }
  
  if (params.tdpMin !== undefined && params.tdpMin >= 15 && params.tdpMin <= 500) {
    cleaned.tdpMin = params.tdpMin
  }
  
  if (params.tdpMax !== undefined && params.tdpMax >= 15 && params.tdpMax <= 500) {
    cleaned.tdpMax = params.tdpMax
  }
  
  if (params.page !== undefined && params.page >= 1 && params.page <= 1000) {
    cleaned.page = params.page
  }
  
  // Clean array parameters
  if (params.cores && params.cores.length > 0) {
    const validCores = params.cores.filter(c => c >= 1 && c <= 128)
    if (validCores.length > 0) {
      cleaned.cores = validCores
    }
  }
  
  if (params.socket && params.socket.length > 0) {
    const validSockets = params.socket.filter(s => s.trim().length > 0)
    if (validSockets.length > 0) {
      cleaned.socket = validSockets
    }
  }
  
  return cleaned
}